import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingSpinner } from '@/components/auth'
import { BookCard, BookForm, FilterPanel, SearchInput } from '@/components/books'
import type { BookFilters } from '@/components/books/FilterPanel'
import { Button, ConfirmDialog, Modal, Pagination } from '@/components/ui'
import { useBooksWithFilters, useCreateBook, useDeleteBook, useUpdateBook } from '@/hooks/books'
import { useUpdateBookGenres } from '@/hooks/genres'
import { useFilterPersistence } from '@/hooks/useFilterPersistence'
import { captureError } from '@/lib/monitoring'
import type { Book, CreateBook } from '@/types'

export function WishlistBooksPage() {
  const { t } = useTranslation()
  const [showForm, setShowForm] = useState(false)
  const [editingBook, setEditingBook] = useState<Book | null>(null)
  const [deletingBook, setDeletingBook] = useState<Book | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 20

  // Stable options object for useFilterPersistence
  const wishlistOptions = useMemo(
    () => ({
      statuses: ['wishlist'],
    }),
    []
  )

  // Load persisted filters - with wishlist status pre-selected
  const { filters, updateFilters, isInitialized } = useFilterPersistence(
    'wishlist',
    wishlistOptions
  )

  // Memoize filters to prevent unnecessary re-renders
  const wishlistFilters = useMemo(
    () => ({
      ...filters,
      statuses: ['wishlist'], // Always filter for wishlist only
    }),
    [filters]
  )

  // Use new database-level filtering and pagination
  const {
    data: booksData,
    isLoading,
    error,
  } = useBooksWithFilters({
    filters: wishlistFilters,
    page: currentPage,
    pageSize,
    enabled: isInitialized,
  })

  const createBookMutation = useCreateBook()
  const updateBookMutation = useUpdateBook()
  const updateBookGenresMutation = useUpdateBookGenres()
  const deleteBookMutation = useDeleteBook()

  // Reset to page 1 when filters change
  const handleFiltersChange = (newFilters: BookFilters) => {
    updateFilters({
      ...newFilters,
      statuses: ['wishlist'], // Ensure wishlist status is maintained
    })
    setCurrentPage(1)
  }

  const handleSearchChange = (search: string) => {
    updateFilters({ ...filters, search })
    setCurrentPage(1)
  }

  const handleCreateBook = async (data: CreateBook, genreIds: string[]) => {
    try {
      const bookData = { ...data, status: 'wishlist' as const }
      const newBook = await createBookMutation.mutateAsync(bookData)

      // Update genres if any
      if (genreIds.length > 0) {
        await updateBookGenresMutation.mutateAsync({
          bookId: newBook.id,
          genreIds,
        })
      }

      setShowForm(false)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to create book' })
    }
  }

  const handleUpdateBook = async (data: CreateBook, genreIds: string[]) => {
    if (!editingBook) return

    try {
      // Update book data
      await updateBookMutation.mutateAsync({
        bookId: editingBook.id,
        data: {
          title: data.title,
          author: data.author,
          isbn: data.isbn,
          status: data.status,
          rating: data.rating,
          notes: data.notes,
          cover_url: data.cover_url,
          start_date: data.start_date,
          end_date: data.end_date,
          ownership: data.ownership,
          borrowed_from: data.borrowed_from,
        },
      })

      // Update genres
      await updateBookGenresMutation.mutateAsync({
        bookId: editingBook.id,
        genreIds,
      })

      setEditingBook(null)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to update book' })
    }
  }

  const handleDeleteBook = async () => {
    if (!deletingBook) return

    try {
      await deleteBookMutation.mutateAsync(deletingBook.id)
      setDeletingBook(null)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to delete book' })
    }
  }

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-error">
          {t('common.error')}: {error.message}
        </p>
      </div>
    )
  }

  const books = booksData?.books || []
  const totalCount = booksData?.totalCount || 0
  const pageCount = booksData?.pageCount || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('wishlist.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {!isInitialized || isLoading
              ? t('common.loading')
              : totalCount > 0
                ? t('books.wishlistCount', { count: totalCount })
                : t('wishlist.description')}
          </p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="w-full sm:w-auto"
          disabled={showForm || !!editingBook}
        >
          {t('wishlist.addToWishlist')}
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar and Filter Toggle */}
        <div className="flex gap-3">
          <SearchInput
            value={filters.search}
            onChange={handleSearchChange}
            placeholder={t('search.searchByTitleAuthor')}
            className="flex-1"
          />

          <FilterPanel
            filters={filters}
            onFiltersChange={handleFiltersChange}
            compact={true}
            hideStatusFilter
          />
        </div>
      </div>

      {/* Add/Edit Book Modal */}
      <Modal
        isOpen={showForm || !!editingBook}
        onClose={() => {
          setShowForm(false)
          setEditingBook(null)
        }}
        title={editingBook ? t('books.editBook') : t('wishlist.addToWishlist')}
        size="lg"
      >
        <BookForm
          initialData={editingBook || { status: 'wishlist' }}
          initialGenres={editingBook?.book_genres?.map((bg) => bg.genre.id) || []}
          onSubmit={editingBook ? handleUpdateBook : handleCreateBook}
          onCancel={() => {
            setShowForm(false)
            setEditingBook(null)
          }}
          isLoading={
            createBookMutation.isPending ||
            updateBookMutation.isPending ||
            updateBookGenresMutation.isPending
          }
          hideStatusField={true}
        />
      </Modal>

      {/* Books List */}
      <div className="mt-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              {t('common.loading')}
            </div>
          </div>
        ) : books && books.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {books.map((book) => (
                <BookCard
                  key={book.id}
                  book={book}
                  onEdit={(book) => setEditingBook(book)}
                  onDelete={(book) => setDeletingBook(book)}
                  context="wishlist"
                />
              ))}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={pageCount}
                onPageChange={setCurrentPage}
                totalItems={totalCount}
                pageSize={pageSize}
                className="mt-8"
              />
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">
              <svg
                className="mx-auto h-16 w-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              {filters.search ||
              filters.genres.length > 0 ||
              filters.ratingRange.min > 0 ||
              filters.ratingRange.max < 5
                ? t('common.noResultsMessage')
                : t('wishlist.emptyMessage')}
            </h3>
            <p className="text-muted-foreground mb-6">
              {filters.search ||
              filters.genres.length > 0 ||
              filters.ratingRange.min > 0 ||
              filters.ratingRange.max < 5
                ? t('books.tryAdjustingFilters')
                : t('wishlist.description')}
            </p>
            <Button onClick={() => setShowForm(true)}>{t('wishlist.addFirstBook')}</Button>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingBook}
        title={t('books.confirmDelete.title')}
        message={t('books.confirmDelete.message', { title: deletingBook?.title })}
        confirmText={t('common.delete')}
        onConfirm={handleDeleteBook}
        onCancel={() => setDeletingBook(null)}
        variant="danger"
      />
    </div>
  )
}
